/* eslint-disable @typescript-eslint/no-explicit-any */
import { linkCxActionRecords, unlinkCxActionRecords } from "features/cx-actions/reducer/CxActionActions";
import { linkRecordsOnMap } from "features/journey-map/reducer/CJMActions";
import { linkOpportunityRecords, unlinkOpportunityRecords } from "features/opportunity/reducer/OpportunityActions";
import { linkSolutionRecords, unlinkSolutionRecords } from "features/solutions/reducer/SolutionsActions";
import { useState } from "react";
import { Container, Tab, Tabs } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import DataLoader from "shared/components/data-loader";
import { TabTitleContext } from "shared/components/Relations/RelationManager";
import { useItem } from "shared/dynamic-imports";
import { IconDispatcher } from "shared/Icons";
import { v4 as uuid } from "uuid";
import { NoData, NoDataIcon, NoDataText } from "../cx-components.style";
import { TabData, TabsData, TypesTabs } from "./tabs-data";

type Props = {
	disabled: boolean;
	stageId?: string;
	entityType: (typeof TypesTabs)[keyof typeof TypesTabs];
	defaultKey: number;
	onClose?: () => void;
};

export default function Relations({ disabled, stageId, entityType, defaultKey = 0, onClose }: Props): JSX.Element {
	const { t } = useTranslation();
	const { pathname, search } = useLocation();
	const { cjmIdStr, personaIdStr } = search?.getAllMapsParams() || {};
	const { currentItem, handleChange } = useItem();
	const [key, setKey] = useState<string | undefined>(TabsData[defaultKey]?.label);
	const [count, setCount] = useState<any>(null);
	const swimlanes = useSelector(({ maps }: any) => maps.current_map?.cjmSwimLanes);
	const allSwimlanesDetails = useSelector(({ maps }: any) => maps.current_map?.cjmSwimLaneDetails);

	const { displayedTabs, functions } = getEntityData(entityType) || {}; // get the tabs and functions based on the entity type

	const link = (props: any) => {
		const { recIds, entityType, attributeName, swimlaneType, cxAttributeID } = props;
		functions.link({ recEntityName: entityType, recIds, id: currentItem.id }); // link records in the backend
		handleChange(attributeName, [...(currentItem?.[attributeName] || []), ...recIds]);
		if (cjmIdStr && swimlaneType && stageId && cxAttributeID) {
			// link the opp.sol.action to the map
			const swlimlane = swimlanes?.find((swimLane: any) => swimLane.type === swimlaneType);
			const relatedIdsOnThisStage = allSwimlanesDetails?.[swlimlane?.key]?.[stageId]?.items?.map((item: any) => item?.[cxAttributeID]) || [];
			if (relatedIdsOnThisStage?.includes(recIds?.[0])) return;

			const params = {
				cjmId: cjmIdStr,
				customSwimLaneId: swlimlane?.key || uuid(),
				stageId,
				swimLaneItemIds: [uuid()],
				objIds: recIds,
				objType: swimlaneType,
				attributeId: cxAttributeID,
				personaId: personaIdStr,
			};

			linkRecordsOnMap(params);
		}
	};

	const unlink = (props: any) => {
		const { recIds, entityType, attributeName } = props;
		functions.unlink({ recEntityName: entityType, recIds, id: currentItem.id }); // unlink records in the backend
		handleChange(
			attributeName,
			currentItem[attributeName]?.filter((id: string) => !recIds.includes(id))
		); // update the state
	};

	return (
		<TabTitleContext.Provider value={{ count, setCount }}>
			<Tabs activeKey={key} onSelect={(k: string | null) => setKey(k as string)} className="mb-3 fs2">
				{TabsData?.filter((tab: TabData) => displayedTabs?.includes(tab?.type as string)) // filter tabs based on the entity type
					?.map(({ AddComponent, Card, componentProps, cardProps, label, type, swimlaneType }: TabData) => {
						const attribute = componentProps?.attributeName || cardProps?.attributeName;
						const number = count?.[attribute] || currentItem?.[attribute]?.length || 0;
						const swimlaneKeyToAddItems = swimlanes?.find((swimlane: any) => swimlane?.type === swimlaneType)?.swimlaneKey;

						return (
							<Tab key={type} eventKey={label} title={`${t(label)} (${number})`}>
								<DataLoader {...componentProps} disabledSpinner={true} disabled={pathname === "/analytics" || pathname === "/EditMap"}>
									{AddComponent && (
										<AddComponent
											{...componentProps}
											isSwitchDisabled={disabled}
											disabled={disabled}
											swimlaneKey={swimlaneKeyToAddItems}
											stageId={stageId}
											cxItem={currentItem}
											selected={currentItem?.[attribute]}
											isCloseAfterCreate={true}
											link={(id: string) => link({ recIds: [id], ...componentProps, swimlaneType })}
										/>
									)}
									<div />
									<Container className="p-3">
										{cardProps?.mapping === false ? (
											<Card item={currentItem} attribute={attribute} {...cardProps} />
										) : (
											currentItem?.[attribute]?.map((id: string, index: number) => (
												<div key={id}>
													<Card
														{...cardProps}
														recordId={id}
														unlink={(id: string) => unlink({ recIds: [id], ...componentProps })}
														disable={disabled}
														index={index}
														stageId={stageId}
														onClose={onClose}
													/>
												</div>
											))
										)}
										{number === 0 ? (
											<NoData>
												<NoDataIcon>{IconDispatcher(componentProps?.noDataIcon, "", { size: "3rem", style: "thin" })}</NoDataIcon>
												<NoDataText>{t(componentProps?.noDataText)}</NoDataText>
											</NoData>
										) : null}
									</Container>
								</DataLoader>
							</Tab>
						);
					})}
			</Tabs>
		</TabTitleContext.Provider>
	);
}

export function getEntityData(entityType: string) {
	const { Insights, RelatedCjms, Solutions, ActionsList, Project, Opportunities } = TypesTabs;
	switch (entityType) {
		case "cem_cxopportunity": {
			const displayedTabs = [Insights, RelatedCjms, Solutions, ActionsList, Project];
			const functions = {
				link: linkOpportunityRecords,
				unlink: unlinkOpportunityRecords,
			};
			return { displayedTabs, functions };
		}
		case "cem_cxsolution": {
			const displayedTabs: (typeof TypesTabs)[keyof typeof TypesTabs][] = [Insights, RelatedCjms, Opportunities, ActionsList, Project];
			const functions: any = {
				link: linkSolutionRecords,
				unlink: unlinkSolutionRecords,
			};
			return { displayedTabs, functions };
		}
		case "cem_cxactiontask": {
			const displayedTabs: (typeof TypesTabs)[keyof typeof TypesTabs][] = [Insights, RelatedCjms, Solutions, Opportunities, Project];
			const functions: any = {
				link: linkCxActionRecords,
				unlink: unlinkCxActionRecords,
			};
			return { displayedTabs, functions };
		}
		case TypesTabs?.CustomLibraries: {
			const displayedTabs: (typeof TypesTabs)[keyof typeof TypesTabs][] = [RelatedCjms];
			const functions: any = {
				link: () => {},
				unlink: () => {},
			};
			return { displayedTabs, functions };
		}
		default:
			return {};
	}
}
